import { useCreateLocation } from '@hakimo-ui/hakimo/data-access';
import {
  CreateLocationPayload,
  LocationFormData,
} from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { Button } from '@hakimo-ui/shared/ui-base';
import { toast } from '@hakimo-ui/hakimo/util';
import { useMemo, useState } from 'react';
import { LocationFormCard } from './cards/LocationFormCard';

interface Props {
  tenantId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export function CreateLocations(props: Props) {
  const { tenantId, onCancel, onSuccess } = props;
  const [locationForm, setLocationForm] = useState<LocationFormData>({
    name: '',
    street: '',
    city: '',
    state: '',
    country: '',
    timezone: '',
  });

  const onCreateLocationSuccess = () => {
    toast('Location created successfully', { type: 'success' });
    onSuccess();
  };

  const onCreateLocationError = (error: Error) => {
    toast(error.message || 'Failed to create location', { type: 'error' });
  };

  const createLocationMutation = useCreateLocation(
    tenantId,
    onCreateLocationSuccess,
    onCreateLocationError
  );

  const handleLocationFormChange = (data: LocationFormData) => {
    setLocationForm(data);
  };

  const handleSaveLocation = () => {
    if (!locationForm.name.trim()) {
      toast('Location name is required', { type: 'error' });
      return;
    }

    const payload: CreateLocationPayload = {
      name: locationForm.name,
      city: locationForm.city,
      state: locationForm.state,
      country: locationForm.country,
      timezone: locationForm.timezone,
      isManned: true,
      isDeadzoneEnabled: true,
      ...(locationForm.street.trim() && { description: locationForm.street }),
    };

    createLocationMutation.mutate(payload);
  };

  const isSaveLocationDisabled =
    !locationForm.name.trim() || createLocationMutation.isLoading;

  const pageActions = useMemo(
    () => [
      <Button
        key="save"
        variant="primary"
        onClick={handleSaveLocation}
        disabled={isSaveLocationDisabled}
        loading={createLocationMutation.isLoading}
      >
        Save
      </Button>,
      <Button
        key="cancel"
        onClick={onCancel}
        disabled={createLocationMutation.isLoading}
      >
        Cancel
      </Button>,
    ],
    [
      handleSaveLocation,
      isSaveLocationDisabled,
      createLocationMutation.isLoading,
      onCancel,
    ]
  );

  return (
    <Page
      title="New"
      subtitle="Tenant / Location"
      onClickBack={onCancel}
      pageActions={pageActions}
    >
      <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg h-full px-4 py-8">
        <div className="mx-auto w-full max-w-6xl">
          <LocationFormCard
            formData={locationForm}
            onChange={handleLocationFormChange}
          />
        </div>
      </div>
    </Page>
  );
}

export default CreateLocations;
